import type { Edge, Node } from "@xyflow/react";
import type { GroupLocator } from "../../../stores/automation/utils/action-group-walk";

// eslint-disable-next-line import/no-unused-modules -- consumed by FlowCanvas node components (Task 5)
export type ActionCardGroupType = "actions" | "loop" | "branches";

/** Legacy Sidebar section headers ("When / this happens", "Then / do this").
 *  Carried in node data so the label renders INSIDE the RF node — the node's
 *  measured height then includes the label and the layout adapts to it. */
export type SectionLabelKind = "when" | "then";

export type FlowNodeData = (
  | { kind: "trigger" }
  | {
      kind: "action";
      actionHash: string;
      groupType: ActionCardGroupType;
      locator: GroupLocator;
    }
  | { kind: "branchesBlock"; instanceId: number }
  | { kind: "branchHeader"; instanceId: number; branchHash: string }
  | { kind: "loopGroup"; instanceId: number }
  | { kind: "addAction"; locator: GroupLocator }
  // A standalone "Then / do this" header. Kept as its OWN non-draggable node
  // (rather than baked into the first action) so dragging the first action
  // card never drags the header and every action card shares the same measured
  // geometry — the reorder math compares uniform card boxes.
  | { kind: "sectionLabel"; label: SectionLabelKind }
) & { sectionLabel?: SectionLabelKind };

// eslint-disable-next-line import/no-unused-modules -- consumed by the FlowCanvas nodeTypes map (Task 6)
export type FlowNodeType = FlowNodeData["kind"];

export type FlowNode = Node<
  Record<string, unknown> & FlowNodeData,
  FlowNodeType
>;

// eslint-disable-next-line import/no-unused-modules -- consumed by the FlowCanvas edgeTypes map (Task 6)
export type FlowEdgeType = "step" | "branchFan";

export type FlowEdge = Edge & { type: FlowEdgeType };

/** Nested layout spec produced by build-graph, consumed by layout.ts.
 *  Mirrors the visual structure: vertical stacks of cards, branch fans
 *  (a block card over a row of columns), and loop group containers. */
export type LayoutItem =
  | { kind: "card"; nodeId: string }
  | { kind: "collapsedLoop"; nodeId: string }
  | { kind: "fan"; blockNodeId?: string; columns: LayoutColumn[] }
  | { kind: "group"; nodeId: string; items: LayoutItem[] };

export interface LayoutColumn {
  headerNodeId: string;
  items: LayoutItem[];
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
  layout: LayoutItem[];
}

const locatorKey = (locator: GroupLocator): string => {
  switch (locator.kind) {
    case "root":
      return "root";
    case "loop":
      return `loop:${locator.instanceId}`;
    case "branch":
      return `branch:${locator.instanceId}:${locator.branchHash}`;
  }
};

export const TRIGGER_NODE_ID = "trigger";

export const SECTION_LABEL_ID_PREFIX = "sectionLabel:";

export const nodeId = {
  action: (hash: string): string => `action:${hash}`,
  branchesBlock: (instanceId: number): string => `branchesBlock:${instanceId}`,
  branchHeader: (instanceId: number, branchHash: string): string =>
    `branchHeader:${instanceId}:${branchHash}`,
  loopGroup: (instanceId: number): string => `loopGroup:${instanceId}`,
  addAction: (locator: GroupLocator): string =>
    `addAction:${locatorKey(locator)}`,
  sectionLabel: (label: SectionLabelKind, locator: GroupLocator): string =>
    `${SECTION_LABEL_ID_PREFIX}${label}:${locatorKey(locator)}`,
};
